import { useState, type ReactNode } from 'react';
import { CaretDownIcon, XIcon } from '@phosphor-icons/react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { activeAxisCount, type FlightFilterState } from '../filter_logic.js';
import type { FlightOfferSource } from '../flight_offer_source.js';
import type { FlightFilterCopy } from './filter_copy.js';
import type { FlightFilterCabin } from './flight_filter_panel.js';

/** O cromo do painel de cortes do voo — o cabeçalho (`Filtros` + `Redefinir`) e
 *  a seção dobrável — fora de `filter_panel.tsx` pelo teto de linhas dele. */
/** O cabeçalho também nasce quando só há CABINE (lista vazia, nenhum corte a
 *  desfazer), na ESPERA (o que está embaixo é esqueleto, e a coluna precisa
 *  nascer com o desenho que vai ter) e na folha que se fecha por ele (o X) — a
 *  menos que a casca de fora o desenhe. */
export function wantsHead(
  header: boolean,
  loading: boolean | undefined,
  axes: boolean,
  cabin: FlightFilterCabin | undefined,
  closable: boolean,
): boolean {
  return header && Boolean(closable || loading || axes || cabin);
}

/** Título + `Redefinir`. Componente próprio só para manter o painel dentro do teto
 *  de complexidade — não tem estado nem regra própria. */
export function PanelHead({
  offers,
  state,
  ceiling,
  loading,
  onReset,
  onClose,
  copy,
}: Readonly<{
  offers: readonly FlightOfferSource[];
  copy: FlightFilterCopy;
  state: FlightFilterState;
  ceiling: number;
  loading: boolean | undefined;
  onReset: () => void;
  /** O X da folha de tela cheia — antes do título, como no `ResultsFiltersPanel`. */
  onClose?: () => void;
}>) {
  return (
    <div className="ffp-head">
      {/* O X da folha (quando há) e o título, juntos à esquerda; o X encosta na
          borda como o do `ResultsFiltersPanel` do kit. */}
      <span className="flex items-center gap-1">
        {onClose && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="-ml-1.5"
            aria-label={copy.close}
            onClick={onClose}
          >
            <XIcon aria-hidden />
          </Button>
        )}
        <span className="ffp-title">{copy.filters}</span>
      </span>
      <button
        type="button"
        className="ffp-reset"
        // Na espera não há corte a desfazer (o pedido já nasceu redefinido), e um
        // `Redefinir` vivo aqui prometeria desfazer a busca. Sem corte com a cabine
        // sozinha em cena vale o mesmo: ele nunca alcançou a cabine.
        disabled={Boolean(loading) || activeAxisCount(offers, state, ceiling) === 0}
        onClick={onReset}
      >
        {copy.reset}
      </button>
    </div>
  );
}

/**
 * Cabeçalho RECOLHÍVEL de seção — o padrão do protótipo em todo eixo (#2110):
 * rótulo à esquerda, caret à direita, conteúdo some sem apagar o corte (o
 * estado do filtro mora no host; recolher é só leitura). Estado local por
 * seção, sempre aberto ao montar — o desenho não pede persistência.
 */
export function PanelSection({
  label,
  labelId,
  children,
}: Readonly<{
  label: string;
  /** Id do rótulo, para um controle da seção se nomear por `aria-labelledby`
   *  (a régua de preço: os polegares do Base UI não herdam o `aria-label` da
   *  raiz e ficavam sem nome — #3239 C6). */
  labelId?: string;
  children: ReactNode;
}>) {
  const [open, setOpen] = useState(true);
  return (
    <section className="ffp-group">
      <button
        type="button"
        className="ffp-gtoggle"
        aria-expanded={open}
        onClick={() => {
          setOpen(!open);
        }}
      >
        <span id={labelId} className="ffp-glabel">
          {label}
        </span>
        <CaretDownIcon size={14} className={`ffp-caret${open ? ' is-open' : ''}`} aria-hidden />
      </button>
      {open && children}
    </section>
  );
}
